import { useEffect } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useLazyGetDeviceMessageHistoryQuery } from "store/api/contractorsApi"
import { UITypes } from "types"
import { ReactComponent as IconMagnifyingGlass } from "assets/svg/icon-magnifying-glass.svg"
import { ReactComponent as IconMessages } from "assets/svg/menu-messages.svg"

import { parseDateField } from "utils/dateUtils"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { EmptyState } from "components/common/EmptyState"
import { Pagination } from "components/common/Pagination"

import { useCustomerDashboard } from "../CustomerDashboardContext"
import { StyledTableWrapper } from "../styled"
import { customerDashboardMessagesHistoryHeaderCells } from "./customerDashboardMessagesHistoryHeaderCells"

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #94a3b8;
  margin: 6px 0 10px;
`

const BackLink = styled.span`
  cursor: pointer;
  color: #2db2c8;

  &:hover {
    text-decoration: underline;
  }
`

const TableArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

const EmptyStateOverlay = styled.div`
  position: absolute;
  inset: 48px 0 0;
  display: flex;
  pointer-events: none;
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 500;
  color: #0f172a;
  margin: 0 0 18px;
`

interface Props {
  onBack: () => void
}

export const CustomerDashboardMessagesHistory = ({ onBack }: Props) => {
  const { devices, currentDevice } = useCustomerDashboard()
  const deviceId = devices[currentDevice]?.id

  const { urlQueryParams } = useUrlQueryParams()
  const [triggerGetMessageHistory, { isFetching, data }] = useLazyGetDeviceMessageHistoryQuery()

  const nonFilterParams = ["page", "perPage", "orderColumn", "orderType"]
  const hasActiveFilters = Object.keys(urlQueryParams).some(
    key => !nonFilterParams.includes(key) && urlQueryParams[key] != null && urlQueryParams[key] !== "",
  )

  useEffect(() => {
    if (!deviceId) return

    const sendRequest = async () => {
      const res = await triggerGetMessageHistory({ deviceId, params: urlQueryParams })
      const error = getErrorMessage(res?.error)
      if (error) toast.error(error)
    }

    sendRequest()
  }, [deviceId, urlQueryParams, triggerGetMessageHistory])

  const tableData: UITypes.TableBodyRow[] = (data?.data ?? []).map(
    message =>
      ({
        title: { value: message.title },
        body: { value: message.body },
        creation_date: { value: parseDateField(message.created_at) },
        delivered_date: { value: parseDateField(message.last_delivery_at, "LLLL") },
        delivery_status: { value: message.delivery_status },
        rowSettings: { id: message.id },
      }) as UITypes.TableBodyRow,
  )

  return (
    <>
      <Breadcrumbs>
        <BackLink onClick={onBack}>Messages</BackLink>
        <span>›</span>
        <span>Message history</span>
      </Breadcrumbs>
      <Title>Message history</Title>
      <TableArea>
        <StyledTableWrapper
          isLoading={isFetching}
          columnsLoaded={true}
          headerCells={customerDashboardMessagesHistoryHeaderCells}
          data={tableData}
          fitContainer={true}
        />
        {!isFetching && tableData.length === 0 && (
          <EmptyStateOverlay>
            <EmptyState
              icon={hasActiveFilters ? <IconMagnifyingGlass /> : <IconMessages fill="currentColor" />}
              title={hasActiveFilters ? "No messages found" : "No message history"}
              description={
                hasActiveFilters
                  ? "No messages match the selected filters for this device."
                  : "No messages have been sent to this device yet."
              }
            />
          </EmptyStateOverlay>
        )}
      </TableArea>
      <Pagination meta={data?.meta} />
    </>
  )
}
